import { useId, type ReactNode } from 'react';
import { RecommendedBadge } from './RecommendedBadge';

interface HbsInputProps {
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'password' | 'tel';
  label?: string;
  placeholder?: string;
  leadingIcon?: ReactNode;
  trailingActions?: ReactNode;
  helper?: string;
  error?: string;
  autoFocus?: boolean;
  required?: boolean;
  readOnly?: boolean;
  name?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Use monospace font for value (e.g. password reveal, codes). */
  mono?: boolean;
  /** Flag the field as recommended: shows a badge by the label and tints the input. */
  recommended?: boolean;
  /**
   * Read-only display override: when provided, the value is rendered by this
   * function (as text) instead of a native `<input>`. Used to show a revealed
   * secret with per-character coloring (see `SecretValue`), which a native
   * input can't do. Only honored alongside `readOnly`.
   */
  renderValue?: (value: string) => ReactNode;
}

export function HbsInput({
  value,
  onChange,
  type = 'text',
  label,
  placeholder,
  leadingIcon,
  trailingActions,
  helper,
  error,
  autoFocus,
  required,
  readOnly,
  name,
  onFocus,
  onBlur,
  mono,
  recommended,
  renderValue,
}: HbsInputProps): JSX.Element {
  const id = useId();
  const message = error ?? helper;
  const showError = !!error;

  return (
    <div>
      {label && (
        // Badge is a sibling of <label>, not a child, so it doesn't become part
        // of the input's accessible name.
        <div className="flex items-center gap-2 mb-1">
          <label
            htmlFor={id}
            className="text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute"
          >
            {label}
          </label>
          {recommended && <RecommendedBadge />}
        </div>
      )}
      <div
        className={`relative flex items-center gap-2 h-12 px-3 rounded-hbs-sm border-[1.5px] transition-colors focus-within:ring-4 focus-within:ring-hbs-primary/20 ${
          recommended ? 'bg-hbs-surface-alt' : 'bg-hbs-surface'
        } ${
          showError
            ? 'border-hbs-danger focus-within:border-hbs-danger'
            : 'border-hbs-border focus-within:border-hbs-primary'
        }`}
      >
        {leadingIcon && (
          <span className="shrink-0 text-hbs-text-mute" aria-hidden="true">
            {leadingIcon}
          </span>
        )}
        {readOnly && renderValue ? (
          // Read-only reveal: render the value as text (spans) so it can be
          // colored per-character. Single-line with horizontal scroll, mirroring
          // the input it replaces.
          <div className="flex-1 min-w-0 overflow-x-auto whitespace-nowrap text-[15px] text-hbs-text">
            {renderValue(value)}
          </div>
        ) : (
          <input
            id={id}
            name={name}
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={placeholder}
            autoFocus={autoFocus}
            required={required}
            readOnly={readOnly}
            className={`flex-1 min-w-0 bg-transparent text-[15px] text-hbs-text placeholder:text-hbs-text-mute focus:outline-none ${
              mono ? 'font-mono tracking-hbs-mono' : 'font-sans'
            }`}
          />
        )}
        {trailingActions && (
          <span className="shrink-0 flex items-center gap-1">{trailingActions}</span>
        )}
      </div>
      {message && (
        <p
          className={`mt-1 text-[12px] ${
            showError ? 'text-hbs-danger' : 'text-hbs-text-mute'
          }`}
        >
          {message}
        </p>
      )}
    </div>
  );
}
